import type { ArchiveEntry } from "./types";

export type FilePreviewKind =
  | "text"
  | "image"
  | "container"
  | "structured"
  | "binary";

export interface DetectedFileFormat {
  id: string;
  label: string;
  category: ArchiveEntry["kind"];
  mime: string;
  preview: FilePreviewKind;
  confidence: "signature" | "extension" | "content" | "unknown";
  container: boolean;
  encryptedContainer: boolean;
  executable: boolean;
  extension?: string;
  details: string[];
}

const ZIP_EXTENSIONS =
  /\.(?:jar|zip|war|ear|apk|aab|ipa|jmod|mcpack|mrpack|docx|xlsx|pptx|odt|ods|odp|vsix|nupkg|whl|xpi|crx)$/i;
const ARCHIVE_EXTENSIONS =
  /\.(?:7z|rar|tar|tgz|tbz2?|txz|gz|gzip|bz2?|xz|lz|lz4|zst|cab|ar|cpio|deb|rpm|pack)$/i;
const NATIVE_EXTENSIONS =
  /\.(?:exe|dll|sys|scr|com|cpl|ocx|so|dylib|dylib64|elf|bin)$/i;
const SCRIPT_EXTENSIONS =
  /\.(?:js|mjs|cjs|jsx|ts|tsx|py|pyw|ps1|psm1|psd1|vbs|vbe|bat|cmd|sh|bash|zsh|fish|lua|rb|php|pl|groovy|gradle|kts|scala|clj|cs|fs|vb|go|rs|c|cc|cpp|cxx|h|hh|hpp|hxx|dart|swift|kt|java|smali|asm|s)$/i;
const CONFIG_EXTENSIONS =
  /\.(?:json|json5|xml|ya?ml|properties|cfg|conf|config|toml|ini|env|desktop|plist|lock)$/i;
const TEXT_EXTENSIONS =
  /\.(?:txt|md|markdown|rst|log|csv|tsv|mf|html?|css|scss|sass|less|sql|graphql|gql|proto|wat|map|license|notice)$/i;
const DOCUMENT_EXTENSIONS =
  /\.(?:pdf|rtf|epub|mobi|chm|doc|xls|ppt)$/i;
const DATABASE_EXTENSIONS =
  /\.(?:sqlite3?|db3?|db-wal|db-shm|mdb|accdb|ldb|leveldb)$/i;
const BYTECODE_EXTENSIONS =
  /\.(?:dex|odex|vdex|oat|wasm|class|pyc|pyo|luac|beam|bc|cil|jarclass|ser)$/i;
const FORENSIC_EXTENSIONS =
  /\.(?:nbt|mca|mcr|pcap|pcapng|cap|spv|jks|jceks|p12|pfx|keystore|der|crt|cer|pem|ole|msg)$/i;
const IMAGE_EXTENSIONS =
  /\.(?:png|jpe?g|gif|webp|bmp|dib|ico|cur|tiff?|avif|heic|heif|svg|dds|ktx2?)$/i;
const MEDIA_EXTENSIONS =
  /\.(?:mp3|wav|ogg|flac|m4a|aac|opus|mp4|m4v|mov|webm|avi|mkv)$/i;
const FONT_EXTENSIONS = /\.(?:ttf|otf|woff2?|eot)$/i;

function extensionOf(path: string) {
  const clean = path.split(/[?#]/, 1)[0];
  const slash = Math.max(clean.lastIndexOf("/"), clean.lastIndexOf("\\"));
  const dot = clean.lastIndexOf(".");
  return dot > slash ? clean.slice(dot).toLowerCase() : "";
}

function pathFormat(
  path: string,
  confidence: DetectedFileFormat["confidence"] = "extension",
): DetectedFileFormat {
  const lower = path.toLowerCase();
  const extension = extensionOf(path);
  const base = {
    confidence,
    encryptedContainer: false,
    executable: false,
    extension: extension || undefined,
    details: [] as string[],
  };

  if (lower.endsWith(".class")) {
    return {
      ...base,
      id: "java-class",
      label: "JVM class file",
      category: "class",
      mime: "application/java-vm",
      preview: "structured",
      container: false,
    };
  }
  if (lower === "meta-inf/manifest.mf") {
    return {
      ...base,
      id: "jar-manifest",
      label: "JAR manifest",
      category: "manifest",
      mime: "text/plain",
      preview: "text",
      container: false,
    };
  }
  if (/meta-inf\/[^/]+\.(?:sf|rsa|dsa|ec)$/i.test(path)) {
    return {
      ...base,
      id: "jar-signature",
      label: "JAR signature metadata",
      category: "signature",
      mime: "application/octet-stream",
      preview: extension === ".sf" ? "text" : "binary",
      container: false,
    };
  }
  if (ZIP_EXTENSIONS.test(path)) {
    const variants: Record<string, [string, string]> = {
      ".jar": ["java-archive", "Java archive"],
      ".war": ["java-war", "Java web archive"],
      ".ear": ["java-ear", "Java enterprise archive"],
      ".apk": ["android-apk", "Android application package"],
      ".aab": ["android-bundle", "Android app bundle"],
      ".ipa": ["apple-ipa", "iOS application package"],
      ".jmod": ["java-jmod", "Java module archive"],
      ".mcpack": ["minecraft-pack", "Minecraft pack"],
      ".mrpack": ["modrinth-pack", "Modrinth pack"],
      ".docx": ["openxml-word", "Open XML Word document"],
      ".xlsx": ["openxml-excel", "Open XML spreadsheet"],
      ".pptx": ["openxml-powerpoint", "Open XML presentation"],
      ".vsix": ["visual-studio-extension", "Visual Studio extension"],
      ".nupkg": ["nuget-package", "NuGet package"],
      ".whl": ["python-wheel", "Python wheel"],
      ".xpi": ["browser-extension", "Browser extension package"],
      ".crx": ["chrome-extension", "Chrome extension package"],
    };
    const [id, label] = variants[extension] ?? ["zip", "ZIP archive"];
    return {
      ...base,
      id,
      label,
      category: "archive",
      mime: extension === ".jar" ? "application/java-archive" : "application/zip",
      preview: "container",
      container: true,
    };
  }
  if (ARCHIVE_EXTENSIONS.test(path)) {
    return {
      ...base,
      id: extension.replace(".", "") || "archive",
      label: `${extension.replace(".", "").toUpperCase() || "Compressed"} archive`,
      category: "archive",
      mime: "application/octet-stream",
      preview: "container",
      container: true,
    };
  }
  if (NATIVE_EXTENSIONS.test(path)) {
    return {
      ...base,
      id: "native-binary",
      label: "Native binary",
      category: "native",
      mime: "application/octet-stream",
      preview: "structured",
      container: false,
      executable: true,
    };
  }
  if (SCRIPT_EXTENSIONS.test(path)) {
    return {
      ...base,
      id: "source-code",
      label: "Source code or script",
      category: "script",
      mime: "text/plain",
      preview: "text",
      container: false,
      executable: true,
    };
  }
  if (CONFIG_EXTENSIONS.test(path)) {
    return {
      ...base,
      id: extension === ".json" ? "json" : "configuration",
      label: extension === ".json" ? "JSON data" : "Configuration file",
      category: "config",
      mime: extension === ".json" ? "application/json" : "text/plain",
      preview: "text",
      container: false,
    };
  }
  if (TEXT_EXTENSIONS.test(path)) {
    return {
      ...base,
      id: "text",
      label: "Text file",
      category: "text",
      mime: "text/plain",
      preview: "text",
      container: false,
    };
  }
  if (DOCUMENT_EXTENSIONS.test(path)) {
    return {
      ...base,
      id: extension === ".pdf" ? "pdf" : "document",
      label: extension === ".pdf" ? "PDF document" : "Document",
      category: "document",
      mime: extension === ".pdf" ? "application/pdf" : "application/octet-stream",
      preview: "structured",
      container: false,
    };
  }
  if (DATABASE_EXTENSIONS.test(path)) {
    return {
      ...base,
      id: "database",
      label: "Database",
      category: "database",
      mime: "application/octet-stream",
      preview: "structured",
      container: false,
    };
  }
  if (FORENSIC_EXTENSIONS.test(path)) {
    const variants: Record<string, [string, string, ArchiveEntry["kind"]]> = {
      ".nbt": ["minecraft-nbt", "Minecraft NBT data", "config"],
      ".mca": ["minecraft-region", "Minecraft Anvil region", "database"],
      ".mcr": ["minecraft-region", "Minecraft region", "database"],
      ".pcap": ["pcap", "Packet capture", "document"],
      ".pcapng": ["pcapng", "PCAP Next Generation capture", "document"],
      ".cap": ["pcap", "Packet capture", "document"],
      ".spv": ["spir-v", "SPIR-V bytecode", "bytecode"],
      ".jks": ["java-keystore", "Java KeyStore", "database"],
      ".jceks": ["java-keystore", "Java Cryptography Extension KeyStore", "database"],
      ".p12": ["pkcs12", "PKCS#12 key store", "database"],
      ".pfx": ["pkcs12", "PKCS#12 key store", "database"],
      ".keystore": ["java-keystore", "Key store", "database"],
      ".ole": ["ole-compound", "OLE compound document", "document"],
      ".msg": ["ole-compound", "Outlook message container", "document"],
    };
    const [id, label, category] = variants[extension] ?? [
      "structured-binary",
      "Structured binary data",
      "binary",
    ];
    return {
      ...base,
      id,
      label,
      category,
      mime: "application/octet-stream",
      preview: "structured",
      container: false,
    };
  }
  if (BYTECODE_EXTENSIONS.test(path)) {
    return {
      ...base,
      id: "bytecode",
      label: "Bytecode or serialized data",
      category: "bytecode",
      mime: "application/octet-stream",
      preview: "structured",
      container: false,
    };
  }
  if (IMAGE_EXTENSIONS.test(path)) {
    const mime =
      extension === ".svg"
        ? "image/svg+xml"
        : extension === ".jpg" || extension === ".jpeg"
          ? "image/jpeg"
          : `image/${extension.replace(".", "") || "unknown"}`;
    return {
      ...base,
      id: "image",
      label: "Image",
      category: "image",
      mime,
      preview: "image",
      container: false,
    };
  }
  if (MEDIA_EXTENSIONS.test(path)) {
    return {
      ...base,
      id: "media",
      label: "Audio or video media",
      category: "media",
      mime: "application/octet-stream",
      preview: "structured",
      container: false,
    };
  }
  if (FONT_EXTENSIONS.test(path)) {
    return {
      ...base,
      id: "font",
      label: "Font",
      category: "font",
      mime: "application/octet-stream",
      preview: "structured",
      container: false,
    };
  }
  return {
    ...base,
    id: "binary",
    label: "Opaque binary data",
    category: "binary",
    mime: "application/octet-stream",
    preview: "binary",
    container: false,
    confidence: "unknown",
  };
}

function startsWith(bytes: Uint8Array, signature: number[], offset = 0) {
  return signature.every((value, index) => bytes[offset + index] === value);
}

function ascii(bytes: Uint8Array, offset: number, length: number) {
  return String.fromCharCode(
    ...bytes.subarray(offset, Math.min(bytes.length, offset + length)),
  );
}

function u2At(bytes: Uint8Array, offset: number) {
  if (offset < 0 || offset + 2 > bytes.length) return 0xffff;
  return (bytes[offset] << 8) | bytes[offset + 1];
}

function plausibleNbtName(bytes: Uint8Array) {
  const length = u2At(bytes, 1);
  if (length > Math.min(512, bytes.length - 3)) return false;
  const name = bytes.subarray(3, 3 + length);
  return Array.from(name).every(
    (byte) => byte === 9 || byte === 10 || byte === 13 || byte >= 0x20,
  );
}

function signatureFormat(
  path: string,
  overrides: Partial<DetectedFileFormat> & Pick<DetectedFileFormat, "id" | "label">,
): DetectedFileFormat {
  return {
    ...pathFormat(path),
    mime: "application/octet-stream",
    preview: "structured",
    category: "binary",
    container: false,
    executable: false,
    ...overrides,
    confidence: "signature",
  };
}

export function detectFileFormat(
  bytes: Uint8Array,
  path = "artifact.bin",
): DetectedFileFormat {
  const byPath = pathFormat(path);
  if (!bytes.length) {
    return {
      ...byPath,
      id: "empty",
      label: "Empty file",
      confidence: "content",
      details: ["0 bytes"],
    };
  }

  if (
    startsWith(bytes, [0x50, 0x4b, 0x03, 0x04]) ||
    startsWith(bytes, [0x50, 0x4b, 0x05, 0x06]) ||
    startsWith(bytes, [0x50, 0x4b, 0x07, 0x08])
  ) {
    return {
      ...byPath,
      id: byPath.container ? byPath.id : "zip",
      label: byPath.container ? byPath.label : "ZIP archive",
      category: "archive",
      mime: byPath.mime === "application/octet-stream" ? "application/zip" : byPath.mime,
      preview: "container",
      confidence: "signature",
      container: true,
      details: ["PKZIP-compatible container"],
    };
  }
  if (startsWith(bytes, [0x4a, 0x4d, 0x01, 0x00])) {
    return signatureFormat(path, {
      id: "java-jmod",
      label: "Java module archive",
      category: "archive",
      preview: "container",
      container: true,
      details: ["JMOD header followed by a ZIP container"],
    });
  }
  if (startsWith(bytes, [0x1f, 0x8b])) {
    return signatureFormat(path, {
      id: "gzip",
      label: "GZIP stream",
      category: "archive",
      preview: "container",
      container: true,
      details: ["DEFLATE-compressed stream"],
    });
  }
  if (startsWith(bytes, [0x37, 0x7a, 0xbc, 0xaf, 0x27, 0x1c])) {
    return signatureFormat(path, {
      id: "7z",
      label: "7-Zip archive",
      category: "archive",
      preview: "container",
      container: true,
      details: ["7z container"],
    });
  }
  if (startsWith(bytes, [0x52, 0x61, 0x72, 0x21, 0x1a, 0x07])) {
    return signatureFormat(path, {
      id: "rar",
      label: "RAR archive",
      category: "archive",
      preview: "container",
      container: true,
      details: ["RAR container"],
    });
  }
  if (startsWith(bytes, [0xfd, 0x37, 0x7a, 0x58, 0x5a, 0x00])) {
    return signatureFormat(path, {
      id: "xz",
      label: "XZ stream",
      category: "archive",
      preview: "container",
      container: true,
      details: ["LZMA2-compressed stream"],
    });
  }
  if (startsWith(bytes, [0x42, 0x5a, 0x68])) {
    return signatureFormat(path, {
      id: "bzip2",
      label: "BZIP2 stream",
      category: "archive",
      preview: "container",
      container: true,
      details: ["BZIP2-compressed stream"],
    });
  }
  if (bytes.length >= 265 && ascii(bytes, 257, 5) === "ustar") {
    return signatureFormat(path, {
      id: "tar",
      label: "TAR archive",
      category: "archive",
      preview: "container",
      container: true,
      details: ["POSIX ustar container"],
    });
  }
  if (startsWith(bytes, [0x4d, 0x53, 0x43, 0x46])) {
    return signatureFormat(path, {
      id: "cab",
      label: "Microsoft Cabinet archive",
      category: "archive",
      preview: "container",
      container: true,
      details: ["MSCF container"],
    });
  }
  if (startsWith(bytes, [0x0a, 0x0d, 0x0d, 0x0a])) {
    return signatureFormat(path, {
      id: "pcapng",
      label: "PCAP Next Generation capture",
      category: "document",
      details: ["PCAPNG section-header block"],
    });
  }
  const captureMagic = Array.from(bytes.subarray(0, 4))
    .map((value) => value.toString(16).padStart(2, "0"))
    .join("");
  const pcapVariants: Record<string, string> = {
    d4c3b2a1: "little-endian microsecond timestamps",
    a1b2c3d4: "big-endian microsecond timestamps",
    "4d3cb2a1": "little-endian nanosecond timestamps",
    a1b23c4d: "big-endian nanosecond timestamps",
  };
  if (pcapVariants[captureMagic]) {
    return signatureFormat(path, {
      id: "pcap",
      label: "Packet capture",
      category: "document",
      details: [pcapVariants[captureMagic]],
    });
  }
  if (
    startsWith(bytes, [0xfe, 0xed, 0xfe, 0xed]) ||
    startsWith(bytes, [0xce, 0xce, 0xce, 0xce])
  ) {
    return signatureFormat(path, {
      id: "java-keystore",
      label: startsWith(bytes, [0xfe, 0xed, 0xfe, 0xed])
        ? "Java KeyStore"
        : "Java Cryptography Extension KeyStore",
      category: "database",
      details: ["Password-protected Java key-store structure"],
    });
  }
  if (startsWith(bytes, [0xd0, 0xcf, 0x11, 0xe0, 0xa1, 0xb1, 0x1a, 0xe1])) {
    return signatureFormat(path, {
      id: "ole-compound",
      label: "OLE compound document",
      category: "document",
      details: ["Microsoft Compound File Binary container"],
    });
  }

  if (startsWith(bytes, [0xca, 0xfe, 0xba, 0xbe])) {
    const major = bytes.length >= 8 ? (bytes[6] << 8) | bytes[7] : 0;
    if (path.toLowerCase().endsWith(".class") || (major >= 45 && major <= 80)) {
      return signatureFormat(path, {
        id: "java-class",
        label: "JVM class file",
        category: "class",
        mime: "application/java-vm",
        details: major ? [`Class-file major version ${major}`] : [],
      });
    }
    return signatureFormat(path, {
      id: "mach-o-universal",
      label: "Mach-O universal binary",
      category: "native",
      executable: true,
      details: ["Fat Mach-O container"],
    });
  }
  if (startsWith(bytes, [0xca, 0xfe, 0xd0, 0x0d])) {
    return signatureFormat(path, {
      id: "pack200",
      label: "Pack200 Java archive",
      category: "bytecode",
      details: ["Legacy Pack200 stream"],
    });
  }
  if (startsWith(bytes, [0x64, 0x65, 0x78, 0x0a, 0x30])) {
    return signatureFormat(path, {
      id: "android-dex",
      label: "Android DEX bytecode",
      category: "bytecode",
      details: [`DEX ${ascii(bytes, 4, 4).replace(/\0/g, "")}`],
    });
  }
  if (startsWith(bytes, [0x6f, 0x61, 0x74, 0x0a])) {
    return signatureFormat(path, {
      id: "android-oat",
      label: "Android OAT bytecode",
      category: "bytecode",
      details: ["ART ahead-of-time bytecode"],
    });
  }
  if (startsWith(bytes, [0x00, 0x61, 0x73, 0x6d])) {
    return signatureFormat(path, {
      id: "webassembly",
      label: "WebAssembly module",
      category: "bytecode",
      mime: "application/wasm",
      details: ["WASM binary module"],
    });
  }
  if (
    startsWith(bytes, [0x03, 0x02, 0x23, 0x07]) ||
    startsWith(bytes, [0x07, 0x23, 0x02, 0x03])
  ) {
    const version =
      bytes.length >= 8
        ? new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength).getUint32(
            4,
            startsWith(bytes, [0x03, 0x02, 0x23, 0x07]),
          )
        : 0;
    return signatureFormat(path, {
      id: "spir-v",
      label: "SPIR-V bytecode",
      category: "bytecode",
      details: version
        ? [`SPIR-V version ${version >>> 16}.${(version >>> 8) & 0xff}`]
        : ["SPIR-V shader bytecode"],
    });
  }
  if (startsWith(bytes, [0x42, 0x43, 0xc0, 0xde])) {
    return signatureFormat(path, {
      id: "llvm-bitcode",
      label: "LLVM bitcode",
      category: "bytecode",
      details: ["LLVM bitcode wrapper"],
    });
  }
  if (startsWith(bytes, [0xac, 0xed, 0x00, 0x05])) {
    return signatureFormat(path, {
      id: "java-serialization",
      label: "Java serialized object stream",
      category: "bytecode",
      details: ["Java Object Serialization stream"],
    });
  }
  if (
    bytes[0] === 0x0a &&
    bytes.length >= 4 &&
    u2At(bytes, 1) <= Math.min(512, bytes.length - 3) &&
    (/\.(?:nbt|dat)$/i.test(path) || plausibleNbtName(bytes))
  ) {
    const nameLength = u2At(bytes, 1);
    const rootName = new TextDecoder("utf-8", { fatal: false }).decode(
      bytes.subarray(3, 3 + nameLength),
    );
    return signatureFormat(path, {
      id: "minecraft-nbt",
      label: "Minecraft NBT data",
      category: "config",
      details: [
        "Uncompressed root compound tag",
        rootName ? `Root name: ${rootName.slice(0, 120)}` : "Unnamed root",
      ],
    });
  }

  if (startsWith(bytes, [0x4d, 0x5a])) {
    const peOffset =
      bytes.length >= 0x40
        ? new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength).getUint32(
            0x3c,
            true,
          )
        : 0;
    const validPe =
      peOffset > 0 &&
      peOffset + 4 <= bytes.length &&
      startsWith(bytes, [0x50, 0x45, 0x00, 0x00], peOffset);
    return signatureFormat(path, {
      id: validPe ? "pe" : "dos-executable",
      label: validPe ? "Windows PE executable" : "DOS executable",
      category: "native",
      mime: "application/vnd.microsoft.portable-executable",
      executable: true,
      details: validPe ? [`PE header at 0x${peOffset.toString(16)}`] : ["MZ header"],
    });
  }
  if (startsWith(bytes, [0x7f, 0x45, 0x4c, 0x46])) {
    return signatureFormat(path, {
      id: "elf",
      label: "ELF binary",
      category: "native",
      executable: true,
      details: [bytes[4] === 2 ? "64-bit ELF" : "32-bit ELF"],
    });
  }
  const machMagic = ascii(bytes, 0, 4);
  if (
    ["\xfe\xed\xfa\xce", "\xce\xfa\xed\xfe", "\xfe\xed\xfa\xcf", "\xcf\xfa\xed\xfe"].includes(
      machMagic,
    )
  ) {
    return signatureFormat(path, {
      id: "mach-o",
      label: "Mach-O binary",
      category: "native",
      executable: true,
      details: ["Apple native executable"],
    });
  }

  if (ascii(bytes, 0, 16) === "SQLite format 3\0") {
    const pageSize =
      bytes.length >= 18 ? ((bytes[16] << 8) | bytes[17]) || 65_536 : 0;
    return signatureFormat(path, {
      id: "sqlite",
      label: "SQLite database",
      category: "database",
      mime: "application/vnd.sqlite3",
      details: pageSize ? [`Page size ${pageSize.toLocaleString()} bytes`] : [],
    });
  }
  if (ascii(bytes, 0, 5) === "%PDF-") {
    return signatureFormat(path, {
      id: "pdf",
      label: "PDF document",
      category: "document",
      mime: "application/pdf",
      details: [`PDF ${ascii(bytes, 5, 3)}`],
    });
  }

  if (startsWith(bytes, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a])) {
    return signatureFormat(path, {
      id: "png",
      label: "PNG image",
      category: "image",
      mime: "image/png",
      preview: "image",
    });
  }
  if (ascii(bytes, 0, 4) === "DDS ") {
    return signatureFormat(path, {
      id: "dds",
      label: "DirectDraw Surface texture",
      category: "image",
      details: ["DDS texture container"],
    });
  }
  if (
    startsWith(bytes, [
      0xab, 0x4b, 0x54, 0x58, 0x20, 0x31, 0x31, 0xbb, 0x0d, 0x0a, 0x1a,
      0x0a,
    ]) ||
    startsWith(bytes, [
      0xab, 0x4b, 0x54, 0x58, 0x20, 0x32, 0x30, 0xbb, 0x0d, 0x0a, 0x1a,
      0x0a,
    ])
  ) {
    return signatureFormat(path, {
      id: startsWith(bytes, [0xab, 0x4b, 0x54, 0x58, 0x20, 0x32])
        ? "ktx2"
        : "ktx",
      label: startsWith(bytes, [0xab, 0x4b, 0x54, 0x58, 0x20, 0x32])
        ? "KTX 2 texture"
        : "KTX texture",
      category: "image",
      details: ["Khronos texture container"],
    });
  }
  if (startsWith(bytes, [0xff, 0xd8, 0xff])) {
    return signatureFormat(path, {
      id: "jpeg",
      label: "JPEG image",
      category: "image",
      mime: "image/jpeg",
      preview: "image",
    });
  }
  if (ascii(bytes, 0, 6) === "GIF87a" || ascii(bytes, 0, 6) === "GIF89a") {
    return signatureFormat(path, {
      id: "gif",
      label: "GIF image",
      category: "image",
      mime: "image/gif",
      preview: "image",
    });
  }
  if (ascii(bytes, 0, 4) === "RIFF" && ascii(bytes, 8, 4) === "WEBP") {
    return signatureFormat(path, {
      id: "webp",
      label: "WebP image",
      category: "image",
      mime: "image/webp",
      preview: "image",
    });
  }
  if (startsWith(bytes, [0x42, 0x4d])) {
    return signatureFormat(path, {
      id: "bmp",
      label: "Bitmap image",
      category: "image",
      mime: "image/bmp",
      preview: "image",
    });
  }
  if (
    startsWith(bytes, [0x00, 0x01, 0x00, 0x00]) &&
    !/\.(?:ttf|otf)$/i.test(path)
  ) {
    return signatureFormat(path, {
      id: "ico",
      label: "Icon image",
      category: "image",
      mime: "image/x-icon",
      preview: "image",
    });
  }
  if (
    startsWith(bytes, [0x00, 0x01, 0x00, 0x00, 0x00]) ||
    ascii(bytes, 0, 4) === "OTTO" ||
    ascii(bytes, 0, 4) === "wOFF" ||
    ascii(bytes, 0, 4) === "wOF2"
  ) {
    return signatureFormat(path, {
      id: "font",
      label: "Font",
      category: "font",
      details: ["OpenType, TrueType, or WOFF font"],
    });
  }

  const textSample = new TextDecoder("utf-8", { fatal: false })
    .decode(bytes.subarray(0, Math.min(bytes.length, 64 * 1024)))
    .replace(/^\uFEFF/, "");
  const ratio = printableRatio(bytes.subarray(0, Math.min(bytes.length, 64 * 1024)));
  if (ratio >= 0.86) {
    const trimmed = textSample.trimStart();
    if (
      (trimmed.startsWith("{") && trimmed.includes(":")) ||
      trimmed.startsWith("[")
    ) {
      try {
        JSON.parse(trimmed);
        return {
          ...pathFormat(path),
          id: "json",
          label: "JSON data",
          category: "config",
          mime: "application/json",
          preview: "text",
          confidence: "content",
          container: false,
          details: ["Valid JSON document"],
        };
      } catch {
        // Continue with a normal text classification.
      }
    }
    if (/^<\?xml\b|^<svg\b/i.test(trimmed)) {
      const svg = /^<svg\b/i.test(trimmed);
      return {
        ...pathFormat(path),
        id: svg ? "svg" : "xml",
        label: svg ? "SVG image source" : "XML document",
        category: svg ? "image" : "config",
        mime: svg ? "image/svg+xml" : "application/xml",
        preview: "text",
        confidence: "content",
        container: false,
        details: [svg ? "SVG is shown as source to avoid active content" : "XML text"],
      };
    }
    return {
      ...pathFormat(path),
      id: byPath.id === "binary" ? "text" : byPath.id,
      label: byPath.id === "binary" ? "UTF-8 text" : byPath.label,
      category: byPath.id === "binary" ? "text" : byPath.category,
      mime: "text/plain",
      preview: "text",
      confidence: byPath.confidence === "unknown" ? "content" : byPath.confidence,
      container: false,
      details: [`${Math.round(ratio * 100)}% printable sample`],
    };
  }

  return {
    ...byPath,
    details: byPath.details.length
      ? byPath.details
      : [`${Math.round(ratio * 100)}% printable sample`],
  };
}

export function classifyEntryPath(
  path: string,
  directory: boolean,
): ArchiveEntry["kind"] {
  if (directory) return "binary";
  return pathFormat(path).category;
}

export function printableRatio(bytes: Uint8Array) {
  if (!bytes.length) return 0;
  let printable = 0;
  for (const byte of bytes) {
    if (
      byte === 0x09 ||
      byte === 0x0a ||
      byte === 0x0d ||
      (byte >= 0x20 && byte <= 0x7e) ||
      byte >= 0xa0
    ) {
      printable += 1;
    }
  }
  return printable / bytes.length;
}

export function byteEntropy(bytes: Uint8Array) {
  if (!bytes.length) return 0;
  const counts = new Uint32Array(256);
  for (const byte of bytes) counts[byte] += 1;
  let value = 0;
  for (const count of counts) {
    if (!count) continue;
    const probability = count / bytes.length;
    value -= probability * Math.log2(probability);
  }
  return Math.round(value * 100) / 100;
}

export function extractPrintableStrings(
  bytes: Uint8Array,
  options: { minimum?: number; limit?: number; maxBytes?: number } = {},
) {
  const minimum = Math.max(3, options.minimum ?? 5);
  const limit = Math.max(1, options.limit ?? 2_000);
  const maxBytes = Math.min(bytes.length, options.maxBytes ?? 4 * 1024 * 1024);
  const found: Array<{ offset: number; encoding: "ASCII" | "UTF-16LE"; value: string }> =
    [];
  let current = "";
  let start = 0;
  const flushAscii = () => {
    if (current.length >= minimum) {
      found.push({ offset: start, encoding: "ASCII", value: current.slice(0, 4_096) });
    }
    current = "";
  };
  for (let index = 0; index < maxBytes && found.length < limit; index += 1) {
    const byte = bytes[index];
    if (byte >= 0x20 && byte <= 0x7e) {
      if (!current) start = index;
      current += String.fromCharCode(byte);
    } else {
      flushAscii();
    }
  }
  flushAscii();

  current = "";
  start = 0;
  for (
    let index = 0;
    index + 1 < maxBytes && found.length < limit;
    index += 2
  ) {
    const low = bytes[index];
    const high = bytes[index + 1];
    if (high === 0 && low >= 0x20 && low <= 0x7e) {
      if (!current) start = index;
      current += String.fromCharCode(low);
    } else {
      if (current.length >= minimum) {
        found.push({
          offset: start,
          encoding: "UTF-16LE",
          value: current.slice(0, 4_096),
        });
      }
      current = "";
    }
  }
  if (current.length >= minimum && found.length < limit) {
    found.push({
      offset: start,
      encoding: "UTF-16LE",
      value: current.slice(0, 4_096),
    });
  }
  return found.sort((left, right) => left.offset - right.offset);
}

export function suggestedRecoveredName(
  originalPath: string,
  format: DetectedFileFormat,
  label = "recovered",
) {
  const original = originalPath.split(/[\\/]/).pop() || "artifact.bin";
  const dot = original.lastIndexOf(".");
  const stem = dot > 0 ? original.slice(0, dot) : original;
  const originalExtension = dot > 0 ? original.slice(dot) : "";
  const inferredExtensions: Record<string, string> = {
    zip: ".zip",
    "java-archive": ".jar",
    "java-class": ".class",
    pe: ".exe",
    elf: ".elf",
    "mach-o": ".macho",
    "mach-o-universal": ".macho",
    gzip: ".gz",
    tar: ".tar",
    "android-dex": ".dex",
    webassembly: ".wasm",
    sqlite: ".sqlite",
    pdf: ".pdf",
    png: ".png",
    jpeg: ".jpg",
    gif: ".gif",
    webp: ".webp",
    json: ".json",
    text: ".txt",
  };
  const extension =
    inferredExtensions[format.id] ??
    (format.id === "binary" ? originalExtension || ".bin" : originalExtension);
  return `${stem}.${label}${extension}`;
}
